import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { AlertTriangle, Clock, Wrench, CheckCircle2, Building2, FileText, TrendingDown, Loader2, RefreshCw, Timer, Shield, Target, DollarSign, Users, Plus, Send, Receipt, Truck, Calendar, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { STATUS_COLORS } from '@/constants/statusColors';

const PRIORITY_STRIP = { emergency:'strip-emergency', high:'strip-high', medium:'strip-medium', low:'strip-low' };
const PIE_COLORS = ['#3B82F6','#F59E0B','#22C55E','#A855F7','#F97316','#06B6D4'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="border rounded-sm px-3 py-2 shadow-xl" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
      <p className="font-['IBM_Plex_Sans'] text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
      <p className="font-['JetBrains_Mono'] text-sm font-bold" style={{ color: 'var(--primary)' }}>
        ${payload[0]?.value?.toLocaleString()}
      </p>
    </div>
  );
};

export default function OpsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/dashboard/ops');
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await api.post('/dashboard/seed');
      toast.success('Demo data loaded!');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="animate-spin" size={28} style={{ color: 'var(--primary)' }} />
    </div>
  );

  const { 
    queue = {}, stats = {}, recent_repairs = [], chart_data = {}, kpis = {}, top_vendors = [],
    warranty_expiring = [], new_leads = {}, top_unpaid_invoices = [], revenue_this_month = {}, pm_due_soon = [],
    new_service_requests = []
  } = data || {};
  const { spend_trend = [], categories = [] } = chart_data;
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="ops-dashboard-page">
      {/* Header bar with Quick Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// DISPATCH CONTROL</p>
          <h1 className="font-['Manrope'] font-extrabold text-2xl tracking-tight" style={{ color: 'var(--text-primary)' }}>Operations Dashboard</h1>
        </div>
        
        {/* Quick Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate('/ops/repairs?new=1')} data-testid="quick-new-repair"
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-['IBM_Plex_Sans'] font-medium transition-all"
            style={{ background: 'var(--primary)', color: 'white' }}>
            <Plus size={14} /> New Repair
          </button>
          <button onClick={() => navigate('/ops/create-invoice')} data-testid="quick-new-invoice"
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-['IBM_Plex_Sans'] font-medium transition-all border"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            <Receipt size={14} /> New Invoice
          </button>
          <button onClick={() => navigate('/ops/intake-inbox')} data-testid="quick-intake"
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-['IBM_Plex_Sans'] font-medium transition-all border"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            <Send size={14} /> Send Invite
          </button>
          <button onClick={handleSeed} disabled={seeding} data-testid="seed-demo-btn"
            className="flex items-center gap-1.5 px-3 py-2 border rounded-md text-xs transition-colors font-['JetBrains_Mono']"
            style={{ borderColor: 'var(--border)', color: 'var(--text-faint)' }}>
            <RefreshCw size={11} className={seeding ? 'animate-spin' : ''} />
            {seeding ? '...' : 'SEED'}
          </button>
        </div>
      </div>

      {/* NEW: Revenue + Leads Summary Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Revenue This Month */}
        <div className="card-slate p-4" data-testid="revenue-this-month">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Revenue (Month)</span>
            <DollarSign size={14} style={{ color: 'var(--success)' }} />
          </div>
          <p className="text-2xl font-bold font-['JetBrains_Mono']" style={{ color: 'var(--success)' }}>{fmt(revenue_this_month.collected)}</p>
          <p className="text-[10px] font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-faint)' }}>
            {fmt(revenue_this_month.invoiced)} invoiced • {revenue_this_month.collected_count || 0} paid
          </p>
        </div>

        {/* New Leads Today */}
        <div className="card-slate p-4 cursor-pointer hover:border-[var(--primary)] transition-colors" 
          onClick={() => navigate('/ops/intake-inbox')} data-testid="new-leads-today">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>New Leads Today</span>
            <Target size={14} style={{ color: 'var(--primary)' }} />
          </div>
          <p className="text-2xl font-bold font-['Manrope']" style={{ color: 'var(--primary)' }}>{new_leads.today || 0}</p>
          <p className="text-[10px] font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-faint)' }}>
            {new_leads.self_serve || 0} self-serve • {new_leads.sales_invite || 0} invites • {new_leads.pending_total || 0} pending
          </p>
        </div>

        {/* Active Clients */}
        <div className="card-slate p-4" data-testid="stat-active_clients">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Active Clients</span>
            <Building2 size={14} style={{ color: 'var(--border)' }} />
          </div>
          <div className="flex items-baseline gap-3">
            <p className="text-2xl font-bold font-['Manrope']" style={{ color: 'var(--text-primary)' }}>{stats.total_active_customers || 0}</p>
            <p className="text-xs font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>signed</p>
          </div>
          <p className="text-[10px] font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-faint)' }}>
            <a href="/ops/pipeline" className="underline hover:opacity-80" style={{ color: 'var(--primary)' }} data-testid="pipeline-link">
              + {stats.pipeline_customers || 0} in pipeline →
            </a>
          </p>
        </div>

        {/* Active Repairs */}
        <div className="card-slate p-4" data-testid="stat-active_repairs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Active Repairs</span>
            <Wrench size={14} style={{ color: 'var(--border)' }} />
          </div>
          <p className="text-2xl font-bold font-['Manrope']" style={{ color: 'var(--text-primary)' }}>{stats.active_repairs || 0}</p>
          <p className="text-[10px] font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-faint)' }}>in progress</p>
        </div>
      </div>

      {/* Work Queue — horizontal strip */}
      <div>
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-3" style={{ color: 'var(--text-faint)' }}>// WORK QUEUE</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'NEEDS ESTIMATE',     value: queue.needs_estimate||0,     icon: Clock,        color:'text-orange-400', border:'border-orange-800/50', bg:'bg-orange-900/10', num:'text-orange-300', glowColor:'rgba(251,146,60,0.35)',  ring: queue.needs_estimate > 0 ? 'ring-pulse-warning' : '' },
            { label: 'REPAIR IN PROGRESS', value: queue.repair_in_progress||0, icon: Wrench,       color:'text-[var(--primary)]', border:'border-[var(--primary)]', bg:'bg-[var(--primary-muted)]', num:'text-[var(--primary)]', glowColor:'rgba(59,130,246,0.3)',   ring: '' },
            { label: 'EST. RECEIVED',      value: queue.estimate_received||0,  icon: CheckCircle2, color:'text-teal-400', border:'border-teal-800/50', bg:'bg-teal-900/10', num:'text-teal-300', glowColor:'rgba(45,212,191,0.3)',  ring: '' },
            { label: 'STALE (24H+)',       value: queue.stale||0,              icon: Timer,        color:'text-[var(--error)]', border:'border-[var(--error)]', bg:'bg-[var(--error-muted)]', num:'text-[var(--error)]', glowColor:'rgba(239,68,68,0.35)',   ring: queue.stale > 0 ? 'ring-pulse-error' : '' },
          ].map(({ label, value, icon: Icon, color, border, bg, num, glowColor, ring }) => (
            <div key={label} className={`rounded-md border ${border} ${bg} ${ring} p-5 relative overflow-hidden`}
              data-testid={`queue-${label.toLowerCase().replace(/ /g,'_').replace(/[()]/g,'')}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.12em]" style={{ color: 'var(--text-faint)' }}>{label}</span>
                <Icon size={14} className={color} strokeWidth={2} />
              </div>
              {/* Number with radial glow behind it */}
              <div className="relative inline-block">
                {value > 0 && (
                  <div className="absolute inset-0 -m-4 rounded-full blur-xl pointer-events-none"
                    style={{ background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)` }} />
                )}
                <p className={`relative text-4xl font-['JetBrains_Mono'] font-bold ${num}`}>{value}</p>
              </div>
              {value > 0 && <div className={`absolute bottom-0 left-0 h-0.5 bg-current ${color} opacity-30`} style={{width: `${Math.min(value * 20, 100)}%`}} />}
            </div>
          ))}
        </div>
      </div>

      {/* NEW: Alerts Row - Warranty + PM Due + Top Unpaid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Warranty Expiring */}
        <div className="card-slate p-4" data-testid="warranty-expiring-widget">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// WARRANTY EXPIRING (30D)</p>
            <Shield size={14} style={{ color: 'var(--warning)' }} />
          </div>
          {warranty_expiring.length === 0 ? (
            <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>No warranties expiring soon</p>
          ) : (
            <div className="space-y-2">
              {warranty_expiring.map((w, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded" style={{ background: 'var(--bg)' }}>
                  <div>
                    <p className="text-xs font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--primary)' }}>{w.unit_number}</p>
                    <p className="text-[10px] font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>{w.customer_name}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs font-['JetBrains_Mono'] font-bold ${w.days_left <= 7 ? 'text-[var(--error)]' : 'text-[var(--warning)]'}`}>
                      {w.days_left} days
                    </p>
                    <p className="text-[9px] font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>{w.warranty_provider || 'N/A'}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PM Due Soon */}
        <div className="card-slate p-4" data-testid="pm-due-widget">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// PM DUE SOON</p>
            <Calendar size={14} style={{ color: 'var(--primary)' }} />
          </div>
          {pm_due_soon.length === 0 ? (
            <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>No PM alerts</p>
          ) : (
            <div className="space-y-2">
              {pm_due_soon.map((pm, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded" style={{ background: 'var(--bg)' }}>
                  <div>
                    <p className="text-xs font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--primary)' }}>{pm.unit_number}</p>
                    <p className="text-[10px] font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>{pm.customer_name}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs font-['JetBrains_Mono'] font-bold ${pm.urgency === 'overdue' ? 'text-[var(--error)]' : 'text-[var(--warning)]'}`}>
                      {pm.miles_since_pm?.toLocaleString()} mi
                    </p>
                    <p className="text-[9px] font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>since last PM</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Unpaid Invoices */}
        <div className="card-slate p-4" data-testid="top-unpaid-widget">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// TOP UNPAID</p>
            <FileText size={14} style={{ color: 'var(--error)' }} />
          </div>
          {top_unpaid_invoices.length === 0 ? (
            <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>All invoices paid!</p>
          ) : (
            <div className="space-y-2">
              {top_unpaid_invoices.map((inv, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded cursor-pointer hover:bg-[var(--primary-muted)] transition-colors" 
                  style={{ background: 'var(--bg)' }}
                  onClick={() => navigate(`/ops/invoices`)}>
                  <div>
                    <p className="text-xs font-['IBM_Plex_Sans'] font-medium" style={{ color: 'var(--text-primary)' }}>{inv.customer_name}</p>
                    <p className="text-[10px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>{inv.invoice_number || 'N/A'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--error)' }}>{fmt(inv.amount)}</p>
                    <p className={`text-[9px] font-['IBM_Plex_Sans'] ${inv.payment_status === 'overdue' ? 'text-[var(--error)]' : 'text-[var(--text-faint)]'}`}>
                      {inv.days_old > 0 ? `${inv.days_old}d old` : 'New'}
                      {inv.payment_status === 'overdue' && ' • OVERDUE'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {stats.unpaid_invoice_count > 5 && (
            <button onClick={() => navigate('/ops/invoices?status=unpaid')} 
              className="w-full mt-3 text-xs font-['JetBrains_Mono'] hover:underline" style={{ color: 'var(--primary)' }}>
              View all {stats.unpaid_invoice_count} unpaid →
            </button>
          )}
        </div>
      </div>

      {/* Charts Row — stacked on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Spend Trend */}
        <div className="md:col-span-8 card-slate p-5">
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-4" style={{ color: 'var(--text-faint)' }}>// SPEND TREND</p>
          {spend_trend.length === 0 ? (
            <div className="h-40 flex items-center justify-center">
              <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-faint)' }}>No invoice data yet — load demo data</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={spend_trend} barSize={28}>
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-faint)', fontSize: 10, fontFamily: 'JetBrains Mono' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--text-faint)', fontSize: 9, fontFamily: 'JetBrains Mono' }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--primary-muted)' }} />
                <Bar dataKey="spend" fill="var(--primary)" radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Category Breakdown — Professional Donut */}
        <div className="md:col-span-4 card-slate p-5">
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-4" style={{ color: 'var(--text-faint)' }}>// BY CATEGORY</p>
          {categories.length === 0 ? (
            <div className="h-40 flex items-center justify-center">
              <p className="font-['IBM_Plex_Sans'] text-xs text-center" style={{ color: 'var(--text-faint)' }}>No repair data</p>
            </div>
          ) : (
            <div className="relative">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={categories} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value" paddingAngle={4} strokeWidth={0}>
                    {categories.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />)}
                  </Pie>
                  <Tooltip
                    formatter={(v, n, p) => [v, p.payload.name]}
                    contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, fontFamily: 'IBM Plex Sans', padding: '8px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}
                    itemStyle={{ color: 'var(--text-primary)' }}
                    labelStyle={{ color: 'var(--text-muted)' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Center label */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ marginTop: '-10px' }}>
                <div className="text-center">
                  <p className="text-2xl font-extrabold font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>{categories.reduce((a, c) => a + c.value, 0)}</p>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Total</p>
                </div>
              </div>
            </div>
          )}
          <div className="mt-3 space-y-1.5">
            {categories.slice(0, 5).map((c, i) => {
              const total = categories.reduce((a, x) => a + x.value, 0);
              const pct = total > 0 ? Math.round((c.value / total) * 100) : 0;
              return (
                <div key={c.name} className="flex items-center gap-2.5">
                  <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-xs font-['IBM_Plex_Sans'] truncate flex-1" style={{ color: 'var(--text-muted)' }}>{c.name}</span>
                  <span className="text-[10px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>{pct}%</span>
                  <span className="text-xs font-['JetBrains_Mono'] font-bold w-6 text-right" style={{ color: 'var(--text-primary)' }}>{c.value}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Performance KPIs */}
      <div>
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-3" style={{ color: 'var(--text-faint)' }}>// PERFORMANCE KPIs</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="card-slate p-4" data-testid="kpi-cost-per-mile">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Cost / Mile</span>
              <TrendingDown size={13} style={{ color: 'var(--success)' }} />
            </div>
            <p className="text-2xl font-bold font-['JetBrains_Mono']" style={{ color: 'var(--success)' }}>${kpis.cost_per_mile?.toFixed(3) || '0.000'}</p>
            <p className="text-[10px] font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-faint)' }}>Industry avg: $0.210</p>
          </div>
          <div className="card-slate p-4" data-testid="kpi-turnaround">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Avg Turnaround</span>
              <Timer size={13} style={{ color: 'var(--primary)' }} />
            </div>
            <p className="text-2xl font-bold font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>{kpis.avg_turnaround_days || 0}<span className="text-sm ml-1" style={{ color: 'var(--text-muted)' }}>days</span></p>
            <p className="text-[10px] font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-faint)' }}>{kpis.avg_turnaround_hours || 0}h avg repair time</p>
          </div>
          <div className="card-slate p-4" data-testid="kpi-vendor-response">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Vendor Response</span>
              <Clock size={13} style={{ color: 'var(--warning)' }} />
            </div>
            <p className="text-2xl font-bold font-['JetBrains_Mono']" style={{ color: kpis.avg_vendor_response_hours > 24 ? 'var(--error)' : 'var(--warning)' }}>{kpis.avg_vendor_response_hours || 0}<span className="text-sm ml-1" style={{ color: 'var(--text-muted)' }}>hrs</span></p>
            <p className="text-[10px] font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-faint)' }}>avg time to estimate</p>
          </div>
          <div className="card-slate p-4" data-testid="kpi-unpaid">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Unpaid Total</span>
              <FileText size={13} style={{ color: 'var(--error)' }} />
            </div>
            <p className="text-2xl font-bold font-['JetBrains_Mono']" style={{ color: stats.unpaid_invoice_total > 0 ? 'var(--error)' : 'var(--text-primary)' }}>{fmt(stats.unpaid_invoice_total)}</p>
            <p className="text-[10px] font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-faint)' }}>{stats.unpaid_invoice_count || 0} invoices outstanding</p>
          </div>
        </div>
      </div>

      {/* ── NEW SERVICE REQUESTS from Customers ── */}
      {new_service_requests.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// NEW CUSTOMER REQUESTS</p>
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold font-['JetBrains_Mono'] animate-pulse"
                style={{ background: 'var(--error)', color: 'white' }}>
                {new_service_requests.length}
              </span>
            </div>
            <button onClick={() => navigate('/ops/repairs?status=open')} className="text-xs hover:underline font-['JetBrains_Mono']" style={{ color: 'var(--primary)' }}>VIEW ALL →</button>
          </div>
          <div className="space-y-2">
            {new_service_requests.map((r) => (
              <div key={r.id}
                onClick={() => navigate(`/ops/repairs/${r.id}`)}
                data-testid={`service-request-${r.id}`}
                className="flex items-center gap-4 p-3 rounded-md border cursor-pointer transition-all hover:border-[var(--primary)] hover:bg-[var(--primary-muted)]"
                style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                {/* Priority dot */}
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.priority === 'emergency' ? 'bg-[var(--error)] animate-pulse' : r.priority === 'high' ? 'bg-orange-400' : 'bg-[var(--primary)]'}`} />
                {/* Truck */}
                <span className="text-xs font-bold font-['JetBrains_Mono'] w-20 flex-shrink-0" style={{ color: 'var(--primary)' }}>
                  {r.truck_unit_number}
                </span>
                {/* Customer */}
                <span className="text-xs font-['IBM_Plex_Sans'] w-36 flex-shrink-0 truncate" style={{ color: 'var(--text-primary)' }}>
                  {r.customer_name}
                </span>
                {/* Issue */}
                <span className="text-xs font-['IBM_Plex_Sans'] flex-1 truncate" style={{ color: 'var(--text-muted)' }}>
                  {r.issue_description}
                </span>
                {/* Priority badge */}
                <span className={`text-[9px] font-bold uppercase font-['JetBrains_Mono'] flex-shrink-0 px-2 py-0.5 rounded border ${r.priority === 'emergency' ? 'border-[var(--error)] text-[var(--error)]' : r.priority === 'high' ? 'border-orange-500 text-orange-400' : 'border-[var(--primary)] text-[var(--primary)]'}`}>
                  {r.priority}
                </span>
                {/* Time */}
                <span className="text-[10px] font-['JetBrains_Mono'] flex-shrink-0 hidden lg:block" style={{ color: 'var(--text-faint)' }}>
                  {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Repairs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// RECENT REPAIR EVENTS</p>
          <button onClick={() => navigate('/ops/repairs')} className="text-xs hover:underline font-['JetBrains_Mono']" style={{ color: 'var(--primary)' }}>VIEW ALL →</button>
        </div>
        <div className="card-slate overflow-hidden">
          <table className="w-full table-comfortable" data-testid="recent-repairs-table">
            <thead>
              <tr>
                {['', 'UNIT', 'CLIENT', 'ISSUE', 'STATUS', 'PRIORITY', 'EST.'].map((h, i) => (
                  <th key={i} className={`${h === '' ? 'w-1 p-0' : ''} ${h === 'ISSUE' ? 'hidden md:table-cell' : ''} ${h === 'EST.' ? 'hidden lg:table-cell' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent_repairs.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-faint)' }}>
                  No events — click SEED to load sample data
                </td></tr>
              ) : recent_repairs.map((r, i) => (
                <tr key={r.id} onClick={() => navigate(`/ops/repairs/${r.id}`)} data-testid={`repair-row-${r.id}`}
                  className={`cursor-pointer transition-colors ${PRIORITY_STRIP[r.priority] || 'strip-low'}`}>
                  <td className="w-1 p-0" />
                  <td className="font-['JetBrains_Mono'] text-xs font-bold" style={{ color: 'var(--primary)' }}>
                    {r.truck_unit_number}
                    {r.is_stale && <span className="ml-2 px-1.5 py-0.5 text-[8px] font-bold uppercase rounded" style={{ background: 'var(--error-muted)', color: 'var(--error)', border: '1px solid var(--error)' }}>STALE</span>}
                  </td>
                  <td className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--text-primary)' }}>{r.customer_name}</td>
                  <td className="text-xs font-['IBM_Plex_Sans'] hidden md:table-cell max-w-xs truncate" style={{ color: 'var(--text-muted)' }}>{r.issue_description}</td>
                  <td>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${STATUS_COLORS[r.status] || STATUS_COLORS.open}`}>
                      {r.status?.replace(/_/g,' ')}
                    </span>
                  </td>
                  <td>
                    <span className={`text-[9px] font-bold uppercase font-['JetBrains_Mono'] ${r.priority==='emergency'?'text-[var(--error)]':r.priority==='high'?'text-orange-400':r.priority==='medium'?'text-[var(--primary)]':'text-[var(--text-faint)]'}`}>
                      {r.priority}
                    </span>
                  </td>
                  <td className="text-xs font-['JetBrains_Mono'] hidden lg:table-cell" style={{ color: 'var(--text-muted)' }}>
                    {r.estimate_amount ? `$${r.estimate_amount.toLocaleString()}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
